import { useQuery } from "@tanstack/react-query"
import { toDictionary } from "@tokenterminal/core/utils/toDictionary"
import { trpc } from "@tokenterminal/core/utils/trpc"
import { apiGET } from "data/api/get"
import fuzzysort from "fuzzysort"
import useMarketSectors from "hooks/trpc/useMarketSectors"
import useProjects from "hooks/trpc/useProjects"
import {
  EXPERIMENTAL_FLAGS,
  useExperimentalFlagEnabled,
} from "hooks/useExperimentalFlags"
import { useAvailableEcosystemMetricIds } from "modules/projects/hooks/useAvailableEcosystemMetricIds"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  additionalCommands,
  initialCommands,
  makeDashboardCommands,
  makeMarketSectorCommands,
  makeMetricCommands,
  makeProductCommands,
  makeProjectCommands,
  makeProjectMetricCommands,
} from "./commands"
import { type Command } from "./types"

export type ScoredCommand = {
  command: Command
  score: number
}

/**
 * A hook that fetches the data needed to generate fuzzy search results.
 */

export const useFuzzyCommandData = () => {
  const { enabled: productPagesEnabled, loading: isProductPagesLoading } =
    useExperimentalFlagEnabled(EXPERIMENTAL_FLAGS.PRODUCT_PAGES_V1)
  const { data: { data: projects = [] } = {}, isLoading: isProjectsLoading } =
    useProjects({ includeMetrics: true })
  const projectsBySlug = useMemo(
    () => toDictionary(projects, "slug"),
    [projects]
  )
  const {
    data: { data: marketSectors = [] } = {},
    isLoading: isMarketSectorsLoading,
  } = useMarketSectors()
  const { data: { data: baseMetrics = [] } = {}, isLoading: isMetricsLoading } =
    useQuery(trpc.metrics.getMetricsConfiguration.queryOptions())
  const metrics = useMemo(() => {
    return baseMetrics.filter((metric) => metric.dashboard_enabled)
  }, [baseMetrics])
  const {
    data: ecosystemMetricIds = [],
    isLoading: isEcosystemMetricIdsLoading,
  } = useAvailableEcosystemMetricIds()
  const { data: { data: products = [] } = {}, isLoading: isProductsloading } =
    useQuery({
      ...trpc.products.getProducts.queryOptions(),
      enabled: isProductPagesLoading || productPagesEnabled,
    })
  const metricsByMetricId = useMemo(
    () => toDictionary(metrics, "metric_id"),
    [metrics]
  )
  const { data: dashboards = [], isLoading: isDashboardsLoading } = useQuery({
    queryKey: ["dashboards"],
    queryFn: async () => {
      const { data } = await apiGET("/v1/dashboards")
      return data
    },
  })

  const isLoading =
    isProjectsLoading ||
    isMarketSectorsLoading ||
    isMetricsLoading ||
    isProductsloading ||
    isDashboardsLoading ||
    isEcosystemMetricIdsLoading

  return useMemo(
    () => ({
      isLoading,
      dashboards,
      ecosystemMetricIds,
      marketSectors,
      metrics,
      metricsByMetricId,
      products,
      projects,
      projectsBySlug,
    }),
    [
      dashboards,
      ecosystemMetricIds,
      isLoading,
      marketSectors,
      metrics,
      metricsByMetricId,
      products,
      projects,
      projectsBySlug,
    ]
  )
}

export const useFuzzySearchResults = () => {
  const {
    dashboards,
    ecosystemMetricIds,
    isLoading,
    marketSectors,
    metrics,
    metricsByMetricId,
    products,
    projects,
    projectsBySlug,
  } = useFuzzyCommandData()

  const searchableItemsRef = useRef<Command[]>([])
  const projectMetricItemsRef = useRef<Command[]>([])
  const [allCommandsById, setAllCommandsById] = useState<Map<
    string,
    Command
  > | null>(null)

  // Create commands and add to fuzzy search indices
  useEffect(() => {
    if (!isLoading) {
      searchableItemsRef.current = [
        ...initialCommands,
        ...additionalCommands,
        ...makeProjectCommands(projects),
        ...makeProductCommands(products, projectsBySlug),
        ...makeMarketSectorCommands(marketSectors),
        ...makeMetricCommands(metrics, ecosystemMetricIds),
        ...makeDashboardCommands(dashboards),
      ]

      projectMetricItemsRef.current = makeProjectMetricCommands(
        projects,
        products,
        projectsBySlug,
        metricsByMetricId,
        ecosystemMetricIds
      )

      const allCommands = [
        ...searchableItemsRef.current,
        ...projectMetricItemsRef.current,
      ]
      setAllCommandsById(
        new Map(allCommands.map((command) => [command.id, command]))
      )
    }
  }, [
    dashboards,
    ecosystemMetricIds,
    isLoading,
    marketSectors,
    metrics,
    metricsByMetricId,
    products,
    projects,
    projectsBySlug,
  ])

  const makeFuzzySearchResults = useCallback(
    (search: string): ScoredCommand[] => {
      if (!search) {
        return []
      }

      if (searchableItemsRef.current && projectMetricItemsRef.current) {
        // Include project-metric results if search includes a space
        const searchableItems = search.includes(" ")
          ? [...searchableItemsRef.current, ...projectMetricItemsRef.current]
          : searchableItemsRef.current

        const fuzzysortResults = fuzzysort.go(search, searchableItems, {
          limit: 100,
          threshold: 0.5,
          keys: [
            "title",
            (item: Command) => (item.keywords ? item.keywords?.join(" ") : ""),
          ],
          // Slight deprioritization for project-metric results
          scoreFn: (item: Fuzzysort.KeysResult<Command>) => {
            if (item.obj.type === "project-metric") {
              return item.score * 0.75
            } else if (
              // A perfect keyword match is a perfect score
              item.obj?.keywords?.find((keyword) => keyword === search)
            ) {
              return 1
            }
            return item.score
          },
        })

        const searchResults = fuzzysortResults
          .map((result) => ({
            command: result.obj,
            score: result.score,
          }))
          // Remove duplicate commands
          .filter(
            (item, index, self) =>
              index === self.findIndex((t) => t.command.id === item.command.id)
          )

        return searchResults
      }

      return []
    },
    []
  )

  return useMemo(
    () => ({
      makeFuzzySearchResults,
      allCommandsById,
      isLoading,
    }),
    [makeFuzzySearchResults, allCommandsById, isLoading]
  )
}
